"use client";

import { sendLoginCode } from "@opensea/embedded-wallet-sdk";
import { CenterAligned, classNames, FormControl, Spinner, TextBody } from "@opensea/ui-kit";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "use-intl";
import { useSnapshot } from "valtio";
import { AuthCode } from "../../../../../components/AuthCode";
import { PasteFromClipboardButton } from "../../../../../components/PasteFromClipboardButton";
import { ResendCode } from "../../../../../components/ResendCode";
import { useTrackError } from "../../../../../providers/TrackErrorProvider";
import { ConnectModalActions, ConnectModalState } from "../../../../../stores/connectModalStore";
const CODE_LENGTH = 6;
const CODE_EXPIRATION_MS = 10 * 60 * 1000;
export const AuthCodeInputStep = ({
  email,
  onSubmit
}) => {
  const t = useTranslations("wallet.AuthCodeInputStep");
  const [code, setCode] = useState("");
  const [lastSendTime, setLastSendTime] = useState(() => new Date());
  const codeExpiration = lastSendTime.getTime() + CODE_EXPIRATION_MS;
  const [codeExpired, setCodeExpired] = useState(false);
  const {
    step
  } = useSnapshot(ConnectModalState);
  const trackError = useTrackError();
  const [submissionError, setSubmissionError] = useState(undefined);
  const submittedRef = useRef(false);
  const handleSubmit = useCallback(async code_0 => {
    // Prevent multiple submissions due to AuthCode remount with complete value after verification step error
    if (submittedRef.current) {
      return;
    }
    try {
      ConnectModalActions.setStep({
        id: "verify-auth-code",
        email
      });
      await onSubmit(code_0);
    } catch (e) {
      trackError(e);
      setSubmissionError(t("invalidAuthCode"));
      ConnectModalActions.setStep({
        id: "enter-email-auth-code",
        email
      });
      // TODO: Track error if not due to invalid code
    } finally {
      submittedRef.current = true;
    }
  }, [submissionError]);
  // Reset submission state when code changes
  useEffect(() => {
    setSubmissionError(undefined);
    submittedRef.current = false;
  }, [code]);
  const pasteFromClipboard = async () => {
    const text = await navigator.clipboard.readText();
    const sanitizedCode = text.trim().split("").filter(n => !Number.isNaN(Number(n)));
    setCode(sanitizedCode.join("").slice(0, CODE_LENGTH));
  };
  const resendCode = async () => {
    await sendLoginCode(email);
    setLastSendTime(new Date());
    setSubmissionError(undefined);
    setCode("");
  };
  if (step.id === "verify-auth-code") {
    return <CenterAligned>
        <Spinner size="sm" />
      </CenterAligned>;
  }
  return <>
      <FormControl error={submissionError} hideLabel label={t("authenticationCode")} overrides={{
      Caption: {
        className: "justify-center"
      }
    }}>
        <AuthCode autoFocus error={codeExpired || Boolean(submissionError)} onChange={setCode} onComplete={handleSubmit} overrides={{
        Inputs: {
          className: classNames("bg-bg-primary")
        }
      }} value={code} />
      </FormControl>
      <TextBody asChild className="mt-4" color="text-secondary" size="xs">
        <p>
          {t("authCodeSent")} <br />
          <TextBody size="xs" weight="semibold">
            {email}
          </TextBody>
        </p>
      </TextBody>
      <PasteFromClipboardButton className="mt-8 md:hidden" onPaste={pasteFromClipboard} />
      <ResendCode codeExpiration={codeExpiration} sendCode={resendCode} setCodeExpired={setCodeExpired} />
    </>;
};